import { useMemo } from 'react'

import { useCreateModal } from '../hooks/useCreateModal'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useIsEditModeSatisfied } from '@/hooks/useIsEditModeSatisfied'
import { useLiveStreamSetup } from '@/hooks/useLiveStreamSetup'
import { usePermissions } from '@/hooks/usePermissions'
import { useRouter } from 'next/compat/router'
import { useTranslation } from 'react-i18next'
import { BiListUl } from 'react-icons/bi'
import { HiOutlineExclamation, HiOutlineOfficeBuilding } from 'react-icons/hi'
import { MdOutlineRouter } from 'react-icons/md'
import { RiShieldFlashLine } from 'react-icons/ri'

import { Flex, Icon } from '@chakra-ui/react'

import { ColorScheme } from '@hivewatch/dark'

import { CaseManagement, Floor } from '@/components/icons'
import { PACS } from '@/components/icons'
import { Rule } from '@/components/icons'
import { CreateCaseModal, DarkCreateCaseModal } from '@/features/case-crud'
import {
  CreateDeviceModal,
  DarkCreateDeviceModal,
} from '@/features/device-crud'
import {
  CreateFacilityModal,
  DarkCreateFacilityModal,
} from '@/features/facility-crud'
import { CreateFloorModal, DarkCreateFloorModal } from '@/features/floor-crud'
import {
  CreateGatewayModal,
  DarkCreateGatewayModal,
} from '@/features/gateway-crud'
import {
  CreateIncidentModal,
  DarkCreateIncidentModal,
} from '@/features/incident-create'
import {
  CreatePACSCredentialsModal,
  DarkCreatePACSCredentialsModal,
} from '@/features/pacs-crud'
import { CreateSOPModal, DarkCreateSOPModal } from '@/features/sop-crud'
import { BUTTON_PRESS, mixpanel, trackEvent } from '@/utils/analytics'
import { CREATE_AUTOMATION, CREATE_INCIDENT_RULE } from '@/utils/routes'

import { CREATE_MODAL_ANALYTICS_LABELS } from '../constants/constants'
import { CreateModalOptions, CreateModalType } from '../types/types'
import { CreateButtonDropdown } from './CreateButtonDropdown'
import { DarkCreateButtonDropdown } from './DarkCreateButtonDropdown'

export const FloorIcon = () => <Icon as={Floor} boxSize='24px' />
export const PACSIcon = () => (
  <Icon as={PACS} boxSize='20px' strokeColor='#fff' />
)
export const RuleIcon = () => <Icon as={Rule} boxSize='32px' />
export const CaseManagementIcon = () => (
  <Icon as={CaseManagement} boxSize='22px' />
)

const getModalComponent = (
  options: CreateModalOptions,
  modalKey: CreateModalType | null,
  isRebrandMenuEnabled: boolean
) => {
  const option = options.find(({ key }) => key === modalKey)
  if (!option) return null
  return isRebrandMenuEnabled
    ? option.darkComponent ?? option.component
    : option.component
}

interface CreateDropdownIProps {
  variant?: string
  /**
   * Renders the dark-package trigger button in the given scheme. Set by any
   * dark header, independent of the menu below it (ENG-30676).
   */
  colorScheme?: ColorScheme
  /**
   * Swaps the menu panel and every creation modal for their rebrand twins
   * (ENG-32861). Resolved by the header that owns this dropdown —
   * `appHeaderRebrand` for the application header, `floorPlanHeaderRebrand`
   * for the floor-plan one — so the two headers roll out independently.
   * While off, a dark trigger still opens the legacy panel and legacy modals,
   * which is the state ENG-30676 shipped.
   */
  isRebrandMenuEnabled?: boolean
}

export const CreateDropdown = ({
  variant,
  colorScheme,
  isRebrandMenuEnabled = false,
}: CreateDropdownIProps) => {
  const {
    shouldEnableCreateDevice,
    shouldEnableCreateFacility,
    shouldEnableCreateFloor,
    shouldEnableCreateGateway,
    shouldEnableCreatePACS,
    shouldEnableCreateIncidentsRule,
    shouldEnableCreateSOP,
    shouldEnableCaseManagement,
    isImplementer,
  } = usePermissions()
  const { hasNoLiveStream, isCamio, isOrangePeel } = useLiveStreamSetup()
  const { openedModal, isOpen, openModal, closeModal } = useCreateModal()
  const router = useRouter()
  const { t } = useTranslation()
  const isPACSSourceSecretsEnabled = useFeatureFlag(
    FeatureFlag.pacsSourceSecrets
  )
  const isIncidentRulesEnabled = useFeatureFlag(FeatureFlag.incidentRules)
  const isIncidentRulesV2Enabled = useFeatureFlag(FeatureFlag.updatedRules)
  const isCaseManagementEnabled = useFeatureFlag(FeatureFlag.caseManagement)
  const isRemoveEditModeEnabled = useFeatureFlag(FeatureFlag.removeEditMode)
  const isEditModeSatisfied = useIsEditModeSatisfied()

  // With the phasing out of Edit Mode, we'll now only gate users based on a
  // permission basis, and should communicate as much.
  const disabledTooltipLabel = isRemoveEditModeEnabled
    ? t('common:no_permission_tooltip')
    : undefined

  const createModalOptions: CreateModalOptions = useMemo(
    () => [
      {
        key: 'incident',
        label: t('common:incident'),
        component: CreateIncidentModal,
        darkComponent: DarkCreateIncidentModal,
        icon: HiOutlineExclamation,
        disabled: false,
        hidden: false,
      },
      {
        key: 'facility',
        label: t('common:facility'),
        component: CreateFacilityModal,
        darkComponent: DarkCreateFacilityModal,
        icon: HiOutlineOfficeBuilding,
        disabled: !shouldEnableCreateFacility || !isEditModeSatisfied,
        hidden: false,
      },
      {
        key: 'floor',
        label: t('common:floor'),
        component: CreateFloorModal,
        darkComponent: DarkCreateFloorModal,
        icon: FloorIcon,
        disabled: !shouldEnableCreateFloor || !isEditModeSatisfied,
        hidden: false,
      },
      {
        key: 'device',
        label: t('common:device'),
        component: CreateDeviceModal,
        darkComponent: DarkCreateDeviceModal,
        icon: RiShieldFlashLine,
        disabled: !shouldEnableCreateDevice || !isEditModeSatisfied,
        hidden: hasNoLiveStream || isCamio,
      },
      {
        key: 'sop',
        label: t('common:sop'),
        component: CreateSOPModal,
        darkComponent: DarkCreateSOPModal,
        icon: BiListUl,
        disabled: !shouldEnableCreateSOP || !isEditModeSatisfied,
        hidden: false,
      },
      {
        key: 'gateway',
        label: t('common:gateway'),
        component: CreateGatewayModal,
        darkComponent: DarkCreateGatewayModal,
        icon: MdOutlineRouter,
        disabled: !shouldEnableCreateGateway || !isEditModeSatisfied,
        hidden: hasNoLiveStream || isCamio,
      },
      {
        key: 'pacs',
        label: t('common:pacs'),
        component: CreatePACSCredentialsModal,
        darkComponent: DarkCreatePACSCredentialsModal,
        icon: PACSIcon,
        disabled: !shouldEnableCreatePACS || !isEditModeSatisfied,
        hidden:
          !shouldEnableCreatePACS ||
          !isOrangePeel ||
          !isPACSSourceSecretsEnabled,
      },
      {
        key: 'rule',
        label: t('automations:create_menu_rule_legacy'),
        component: null,
        icon: RuleIcon,
        disabled: !isEditModeSatisfied,
        // Rules V2 (`updatedRules`) is the go-forward path, so once it's on we
        // drop the legacy entry point for operators and PMs to drive adoption of
        // automations. Implementers keep it to set up AIO rules until those are
        // ported to automations. See ENG-32356.
        hidden:
          !shouldEnableCreateIncidentsRule ||
          !isIncidentRulesEnabled ||
          (isIncidentRulesV2Enabled && !isImplementer),
      },
      {
        key: 'automation',
        label: t('automations:create_menu_automation'),
        component: null,
        icon: RuleIcon,
        disabled: !isEditModeSatisfied,
        hidden: !shouldEnableCreateIncidentsRule || !isIncidentRulesV2Enabled,
      },
      {
        key: 'case',
        label: t('common:case'),
        component: CreateCaseModal,
        darkComponent: DarkCreateCaseModal,
        icon: CaseManagementIcon,
        disabled: false,
        hidden: !shouldEnableCaseManagement || !isCaseManagementEnabled,
      },
    ],
    [
      isEditModeSatisfied,
      shouldEnableCreatePACS,
      shouldEnableCaseManagement,
      isImplementer,
      t,
    ]
  )

  const OpenedModal = getModalComponent(
    createModalOptions,
    openedModal,
    isRebrandMenuEnabled
  )

  const handleModalAction = (modalType: CreateModalType) => {
    if (modalType === 'rule') {
      router.push(CREATE_INCIDENT_RULE)
    } else if (modalType === 'automation') {
      mixpanel.track('Button Press: Create - Automation Rule', {
        is_v2_rule: true,
      })
      router.push(CREATE_AUTOMATION)
    } else {
      trackEvent(
        `${BUTTON_PRESS} Create - ${CREATE_MODAL_ANALYTICS_LABELS[modalType]}`
      )
      openModal(modalType)
    }
  }
  return (
    <>
      <Flex alignItems='center'>
        {isRebrandMenuEnabled && colorScheme ? (
          <DarkCreateButtonDropdown
            colorScheme={colorScheme}
            createModalOptions={createModalOptions}
            disabledTooltipLabel={disabledTooltipLabel}
            openModal={handleModalAction}
          />
        ) : (
          <CreateButtonDropdown
            colorScheme={colorScheme}
            createModalOptions={createModalOptions}
            disabledTooltipLabel={disabledTooltipLabel}
            openModal={handleModalAction}
            variant={variant}
          />
        )}
      </Flex>
      {openedModal !== null && (
        <OpenedModal isOpen={isOpen} onClose={closeModal} />
      )}
    </>
  )
}
